import ThemeToggle from './ThemeToggle';
import ShortUrlModal from './ShortUrlModal';
import PasteModal from './PasteModal';
import SyntaxHighlight from './SyntaxHighlight';
import FileView from './FileView';
import ImageView from './ImageView';
import ConfirmDialog from './ConfirmDialog';
import SettingsPage from './SettingsPage';
import LoginModal from './LoginModal';
import HealthPage from './HealthPage';
import SetupPage from './SetupPage';
import AdminPage from './AdminPage';
import SharedResource from './SharedResource';

import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
  Navigate,
} from 'react-router-dom';
import './App.css';
import './responsive.css';
import NavigationBar from './NavigationBar';
import Sidebar from './Sidebar';
import Toast from './Toast';
import {
  uploadFile, downloadFile, uploadImage, viewImage, createPaste, getPaste, shortenUrl,
  listFiles, listImages, listPastes, listShortUrls,
  deleteFile, deleteImage, deletePaste, deleteShortUrl,
  updateFile, updateImage,
  login, checkSetupRequired, getMe
} from './api';

function PasteView() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paste, setPaste] = useState(null);
  useEffect(() => {
    setLoading(true);
    setError(null);
    getPaste(id)
      .then(res => {
        setPaste(res.data.text);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'Paste not found');
        setLoading(false);
      });
  }, [id]);
  if (loading) return <div className="text-center" style={{ padding: 32 }}>Loading paste...</div>;
  if (error) return <div className="text-center" style={{ padding: 32, color: 'var(--danger)' }}>{error}</div>;
  return (
    <div className="container" style={{ maxWidth: 800, marginTop: '2rem' }}>
      <div className="card">
        <h2 className="mt-0">Paste</h2>
        <pre style={{ fontSize: '1rem', background: 'var(--bg-body)', color: 'var(--text-main)', borderRadius: 'var(--radius)', padding: 16, overflowX: 'auto', border: '1px solid var(--border)' }}>{paste}</pre>
      </div>
    </div>
  );
}

Modal.setAppElement('#root');

function App() {
  // Theme state
  const [theme, setTheme] = useState('light');
  // Short URL modal state
  const [shortUrlModalOpen, setShortUrlModalOpen] = useState(false);
  // Pastes modal state
  const [pasteModalOpen, setPasteModalOpen] = useState(false);
  const [pasteModalLang, setPasteModalLang] = useState('plaintext');
  // Toast notification state
  const [toast, setToast] = useState({ message: '', type: 'info' });
  // Section navigation state
  const [section, setSection] = useState('dashboard');
  // Confirm dialog state
  const [confirm, setConfirm] = useState({ open: false, onConfirm: null, title: '', message: '' });
  
  // User state - Initialize from localStorage
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('currentUser');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  
  const [loginOpen, setLoginOpen] = useState(false);
  const [setupRequired, setSetupRequired] = useState(null); // null = unknown, true = required, false = not required

  // Preferences state - Initialize based on user
  const [prefs, setPrefs] = useState(() => {
    const savedUser = localStorage.getItem('currentUser');
    const username = savedUser ? JSON.parse(savedUser).username : 'guest';
    const savedPrefs = localStorage.getItem(`userPrefs_${username}`);
    return savedPrefs ? JSON.parse(savedPrefs) : { theme: 'light', dashboardStart: true };
  });

  // Apply theme to body
  React.useEffect(() => {
    document.body.classList.toggle('dark-mode', theme === 'dark');
    document.body.classList.toggle('light-mode', theme !== 'dark');
  }, [theme]);

  // Apply theme from prefs
  useEffect(() => {
    setTheme(prefs.theme);
  }, [prefs.theme]);

  // Save prefs whenever they change
  useEffect(() => {
    const username = user ? user.username : 'guest';
    localStorage.setItem(`userPrefs_${username}`, JSON.stringify(prefs));
  }, [prefs, user]);

  // Start section from prefs
  useEffect(() => {
    if (prefs.dashboardStart) setSection('dashboard');
  }, []);

  // Check if setup is required
  useEffect(() => {
    checkSetupRequired()
      .then(res => {
        setSetupRequired(res.data.required);
      })
      .catch(err => {
        console.error('Failed to check setup status', err);
        setSetupRequired(false); // Assume false on error to avoid blocking
      });
  }, []);

  // Helper to show confirm dialog
  const showConfirm = (onConfirm, title, message) => {
    setConfirm({ open: true, onConfirm, title, message });
  };

  // Show toast notification
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  // Dummy login handler (replace with real API call)
  const handleLogin = async (username, password) => {
    try {
      const res = await login(username, password);
      const token = res.data.access_token;
      localStorage.setItem('token', token);
      
      const userRes = await getMe();
      const user = userRes.data;
      setUser(user);
      localStorage.setItem('currentUser', JSON.stringify(user));
      
      // Load user prefs
      const savedPrefs = localStorage.getItem(`userPrefs_${user.username}`);
      if (savedPrefs) {
        setPrefs(JSON.parse(savedPrefs));
      } else {
        // Default prefs for new user
        setPrefs({ theme: 'light', dashboardStart: true });
      }

      setLoginOpen(false);
      showToast('Logged in!', 'success');
      return true;
    } catch (err) {
      showToast('Login failed: ' + (err.response?.data?.detail || err.message), 'error');
      return false;
    }
  };
  
  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('token');
    
    // Reset to guest prefs
    const savedGuestPrefs = localStorage.getItem('userPrefs_guest');
    setPrefs(savedGuestPrefs ? JSON.parse(savedGuestPrefs) : { theme: 'light', dashboardStart: true });
    
    showToast('Logged out', 'info');
  };

  // Delete Handlers
  const handleDeleteFile = (fileId) => {
    showConfirm(() => doDeleteFile(fileId), 'Delete File?', 'Are you sure you want to delete this file?');
  };
  const doDeleteFile = async (fileId) => {
    setConfirm({ ...confirm, open: false });
    try {
      const res = await deleteFile(fileId);
      if (res.data && res.data.deleted) {
        setFileList(prev => prev.filter(f => f.id !== fileId));
        showToast('File deleted!', 'success');
      } else {
        showToast('Failed to delete file', 'error');
      }
    } catch (err) {
      showToast('Delete file error', 'error');
    }
  };
  const handleDeleteImage = (imageId) => {
    showConfirm(() => doDeleteImage(imageId), 'Delete Image?', 'Are you sure you want to delete this image?');
  };
  const doDeleteImage = async (imageId) => {
    setConfirm({ ...confirm, open: false });
    try {
      const res = await deleteImage(imageId);
      if (res.data && res.data.deleted) {
        setImageList(prev => prev.filter(img => img.id !== imageId));
        showToast('Image deleted!', 'success');
      } else {
        showToast('Failed to delete image', 'error');
      }
    } catch (err) {
      showToast('Delete image error', 'error');
    }
  };
  const handleDeletePaste = (pasteId) => {
    showConfirm(() => doDeletePaste(pasteId), 'Delete Paste?', 'Are you sure you want to delete this paste?');
  };
  const doDeletePaste = async (pasteId) => {
    setConfirm({ ...confirm, open: false });
    try {
      const res = await deletePaste(pasteId);
      if (res.data && res.data.deleted) {
        setPasteList(prev => prev.filter(p => p.id !== pasteId));
        showToast('Paste deleted!', 'success');
      } else {
        showToast('Failed to delete paste', 'error');
      }
    } catch (err) {
      showToast('Delete paste error', 'error');
    }
  };
  const handleDeleteShortUrl = (shortUrl) => {
    showConfirm(() => doDeleteShortUrl(shortUrl), 'Delete Short URL?', 'Are you sure you want to delete this short URL?');
  };
  const doDeleteShortUrl = async (shortUrl) => {
    setConfirm({ ...confirm, open: false });
    try {
      const id = shortUrl.split('/').pop();
      const res = await deleteShortUrl(id);
      if (res.data && res.data.deleted) {
        setUrlList(prev => prev.filter(u => u.short !== shortUrl));
        showToast('Short URL deleted!', 'success');
      } else {
        showToast('Failed to delete short URL', 'error');
      }
    } catch (err) {
      showToast('Delete short URL error', 'error');
    }
  };

  // Clipboard copy helper
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Save new short URL from modal
  const handleShortUrlModalSave = async (url) => {
    setShortUrlModalOpen(false);
    if (!url) return;
    const res = await shortenUrl(url);
    setShortUrl(res.data.short_url);
    setUrlList(prev => [...prev, { original: url, short: res.data.short_url }]);
  };

  // Save new paste from modal
  const handlePasteModalSave = async (text, language, options) => {
    setPasteModalOpen(false);
    setPasteModalLang(language);
    if (!text) return;
    try {
      const res = await createPaste(text, options);
      setPasteId(res.data.paste_id);
      setPasteList(prev => [...prev, { id: res.data.paste_id, text, language }]);
      showToast('Paste created!', 'success');
    } catch (err) {
      showToast('Paste creation failed', 'error');
    }
  };

  // Fetch all lists from backend on mount or when user changes
  useEffect(() => {
    if (setupRequired === false && user) {
      listFiles().then(res => {
        setFileList(res.data);
      }).catch(e => console.error("listFiles failed", e));
      listImages().then(res => {
        setImageList(res.data);
      }).catch(e => console.error("listImages failed", e));
      listPastes().then(res => {
        setPasteList(res.data);
      }).catch(e => console.error("listPastes failed", e));
      listShortUrls().then(res => {
        setUrlList(res.data.map(u => ({ original: u.url, short: `${window.location.origin}/u/${u.id}` })));
      }).catch(e => console.error("listShortUrls failed", e));
    }
  }, [setupRequired, user]);

  // File
  const [file, setFile] = useState(null);
  const [fileId, setFileId] = useState('');
  const [downloadedFile, setDownloadedFile] = useState(null);
  const [fileList, setFileList] = useState([]); // {id, name}

  // Image
  const [image, setImage] = useState(null);
  const [imageId, setImageId] = useState('');
  const [viewedImage, setViewedImage] = useState(null);
  const [viewedImageName, setViewedImageName] = useState('');
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageList, setImageList] = useState([]); // {id, name}

  // Paste
  const [pasteText, setPasteText] = useState('');
  const [pasteId, setPasteId] = useState('');
  const [retrievedPaste, setRetrievedPaste] = useState('');
  const [pasteList, setPasteList] = useState([]); // {id, text}

  // URL Shortener
  const [url, setUrl] = useState('');
  const [shortUrl, setShortUrl] = useState('');
  const [urlList, setUrlList] = useState([]); // {original, short}

  // File Handlers
  const handleFileUpload = async (fileObj, options) => {
    if (!fileObj) return;
    try {
      const res = await uploadFile(fileObj, options);
      setFileId(res.data.file_id);
      setFileList(prev => [...prev, { 
        id: res.data.file_id, 
        name: fileObj.name,
        filename: res.data.filename,
        is_public: res.data.is_public,
        expires_at: res.data.expires_at,
        shared_with: res.data.shared_with,
        is_owner: true
      }]);
      showToast('File uploaded!', 'success');
    } catch (err) {
      showToast('File upload failed: ' + (err.response?.data?.detail || err.message), 'error');
      console.error('File upload failed:', err);
    }
  };
  const handleFileUpdate = async (fileId, options) => {
    try {
      await updateFile(fileId, options);
      setFileList(prev => prev.map(f => {
        if (f.id === fileId) {
          return { ...f, ...options };
        }
        return f;
      }));
      showToast('File updated!', 'success');
    } catch (err) {
      showToast('File update failed: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  const handleFileDownload = async (fileId, fileName) => {
    if (!fileId) return;
    try {
      const res = await downloadFile(fileId, fileName);
      const blob = new Blob([res.data]);
      // Use the provided fileName if available
      let filename = fileName || 'downloaded_file';
      const disposition = res.headers['content-disposition'];
      if (disposition) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match) filename = match[1];
      }
      // Create a link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast('File download failed', 'error');
      console.error('File download failed:', err);
    }
  };

  // Image Handlers
  const handleImageUpload = async (imageObj, options) => {
    if (!imageObj) return;
    try {
      const res = await uploadImage(imageObj, options);
      setImageId(res.data.image_id);
      // Use returned is_public or default to true since we force it now
      const isPublic = res.data.is_public !== undefined ? res.data.is_public : true;
      setImageList(prev => [...prev, { 
        id: res.data.image_id, 
        name: imageObj.name, 
        filename: res.data.filename,
        is_public: isPublic,
        expires_at: options.expires_at,
        is_owner: true
      }]);
      showToast('Image uploaded!', 'success');
    } catch (err) {
      showToast('Image upload failed: ' + (err.response?.data?.detail || err.message), 'error');
      console.error('Image upload failed:', err);
    }
  };
  const handleImageUpdate = async (imageId, options) => {
    try {
      await updateImage(imageId, options);
      setImageList(prev => prev.map(img => {
        if (img.id === imageId) {
          return { ...img, ...options };
        }
        return img;
      }));
      showToast('Image updated!', 'success');
    } catch (err) {
      showToast('Image update failed: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  const handleImageView = async (imageId, imageName) => {
    if (!imageId) return;
    try {
      const res = await viewImage(imageId, imageName);
      const blob = new Blob([res.data]);
      let filename = imageName || 'image';
      const disposition = res.headers['content-disposition'];
      if (disposition) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match) filename = match[1];
      }
      const url = window.URL.createObjectURL(blob);
      setViewedImage(url);
      setViewedImageName(filename);
      setImageModalOpen(true);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Unknown error';
      showToast('Image preview failed: ' + msg, 'error');
      console.error('Image preview failed:', err);
    }
  };

  // Paste Handlers
  const handlePasteCreate = async (e) => {
    e.preventDefault();
    if (!pasteText) return;
    try {
      const res = await createPaste(pasteText);
      setPasteId(res.data.paste_id);
      setPasteList(prev => [...prev, { id: res.data.paste_id, text: pasteText }]);
      showToast('Paste created!', 'success');
    } catch (err) {
      showToast('Paste creation failed: ' + (err.response?.data?.detail || err.message), 'error');
      console.error('Paste creation failed:', err);
    }
  };
  const handlePasteGet = async (e) => {
    e.preventDefault();
    if (!pasteId) return;
    // Check local cache first
    const cached = pasteList.find(p => p.id === pasteId);
    if (cached) {
      setRetrievedPaste(cached.text);
      return;
    }
    const res = await getPaste(pasteId);
    setRetrievedPaste(res.data.text);
  };

  // URL Shortener Handlers
  const handleShortenUrl = async (e) => {
    e.preventDefault();
    if (!url) return;
    // Check if already shortened in local list
    const cached = urlList.find(u => u.original === url);
    if (cached) {
      setShortUrl(cached.short);
      return;
    }
    const res = await shortenUrl(url);
    setShortUrl(res.data.short_url);
    setUrlList(prev => [...prev, { original: url, short: res.data.short_url }]);
  };

  // Dashboard stats
  const stats = [
    { label: 'Files', value: fileList.length, icon: '🗂️' },
    { label: 'Images', value: imageList.length, icon: '🖼️' },
    { label: 'Pastes', value: pasteList.length, icon: '📋' },
    { label: 'Short URLs', value: urlList.length, icon: '🔗' },
  ];

  // Recent activity (last 3 of each type)
  const recent = [
    ...fileList.slice(-3).map(f => ({ type: 'File', name: f.name, id: f.id })),
    ...imageList.slice(-3).map(img => ({ type: 'Image', name: img.name, id: img.id })),
    ...pasteList.slice(-3).map(p => ({ type: 'Paste', name: p.text.substring(0, 20), id: p.id })),
    ...urlList.slice(-3).map(u => ({ type: 'Short URL', name: u.short, id: u.short })),
  ].slice(-6).reverse();

  const sidebarSections = user ? [
    { key: 'dashboard', label: 'Dashboard', icon: '🏠' },
    { key: 'files', label: 'Files', icon: '🗂️' },
    { key: 'images', label: 'Images', icon: '🖼️' },
    { key: 'pastes', label: 'Pastes', icon: '📋' },
    { key: 'urls', label: 'Short URLs', icon: '🔗' },
    { key: 'health', label: 'System Health', icon: '🏥' },
    { key: 'settings', label: 'Settings', icon: '⚙️' },
  ] : [
    { key: 'health', label: 'System Health', icon: '🏥' },
  ];

  if (user && user.is_admin) {
    sidebarSections.push({ key: 'admin', label: 'Admin', icon: '🛡️' });
  }

  // Enforce authentication for protected sections
  useEffect(() => {
    if (!user && section !== 'health') {
      setSection('health');
    }
  }, [user, section]);

    return (
      <Router>
        {setupRequired === null ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>
        ) : (
          <>
            <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'info' })} />
            <ConfirmDialog
              open={confirm.open}
              title={confirm.title}
              message={confirm.message}
              onConfirm={() => { confirm.onConfirm && confirm.onConfirm(); }}
              onCancel={() => setConfirm({ ...confirm, open: false })}
            />
            <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} onLogin={handleLogin} />
            <Modal
              isOpen={imageModalOpen}
              onRequestClose={() => { setImageModalOpen(false); if (viewedImage) window.URL.revokeObjectURL(viewedImage); }}
              contentLabel="Image Preview"
              style={{ content: { maxWidth: 600, margin: 'auto', borderRadius: 8, padding: 24, textAlign: 'center' } }}
            >
              <h2 style={{ marginTop: 0 }}>{viewedImageName}</h2>
              {viewedImage && <img src={viewedImage} alt={viewedImageName} style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8 }} />}
              <div style={{ marginTop: 16 }}>
                <button onClick={() => { setImageModalOpen(false); if (viewedImage) window.URL.revokeObjectURL(viewedImage); }} style={{ padding: '8px 24px', borderRadius: 6, background: '#444b5a', color: '#fff', border: 'none' }}>Close</button>
              </div>
            </Modal>
            <Routes>
              {setupRequired && (
                <>
                  <Route path="/setup" element={<SetupPage onSetupComplete={() => { setSetupRequired(false); setLoginOpen(true); }} />} />
                  <Route path="*" element={<Navigate to="/setup" replace />} />
                </>
              )}
              {!setupRequired && (
                <>
                  <Route path="/setup" element={<Navigate to="/" replace />} />
                  <Route path="/paste/:id" element={<PasteView />} />
                  <Route path="/s/:type/:id" element={<SharedResource user={user} onLogin={() => setLoginOpen(true)} />} />
                  <Route path="/s/:type/:id/:filename" element={<SharedResource user={user} onLogin={() => setLoginOpen(true)} />} />
                  <Route path="*" element={
                    <div className="app-layout">
                      <Sidebar currentSection={section} onSectionChange={setSection} className="sidebar" sections={sidebarSections} />
                      <div className="main-content">
                        <div className="navigation-bar" style={{ 
                          width: '100%', 
                          background: 'var(--bg-surface)',
                          borderBottom: '1px solid var(--border)',
                          padding: '0.75rem 2rem',
                          boxShadow: 'var(--shadow-sm)',
                          position: 'sticky',
                          top: 0,
                          zIndex: 100,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          height: '64px',
                          boxSizing: 'border-box'
                        }}>
                          <NavigationBar user={user} onLogin={() => setLoginOpen(true)} onLogout={handleLogout} />
                        </div>
                        <div className="container">
                          {section === 'health' && <HealthPage />}
                          {user && section === 'settings' && (
                            <SettingsPage 
                              prefs={prefs} 
                              setPrefs={setPrefs} 
                              theme={theme} 
                              setTheme={setTheme}
                              showToast={showToast}
                            />
                          )}
                          {user && section === 'admin' && user.is_admin && <AdminPage />}
                          {user && section === 'dashboard' && (
                            <>
                              <h2 className="text-center" style={{ margin: '2rem 0 1rem' }}>Dashboard</h2>
                              <div className="dashboard-stats">
                                {stats.map(stat => (
                                  <div key={stat.label} className="stat-card">
                                    <div className="stat-icon">{stat.icon}</div>
                                    <div className="stat-value">{stat.value}</div>
                                    <div className="stat-label">{stat.label}</div>
                                  </div>
                                ))}
                              </div>
                              {user && user.is_admin && (
                                <>
                                  <h3 style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>Recent Activity</h3>
                                  <ul className="list-group">
                                    {recent.length === 0 && <li className="list-item" style={{ justifyContent: 'center', color: 'var(--text-secondary)' }}>No recent activity yet.</li>}
                                    {recent.map((item, idx) => (
                                      <li key={idx} className="list-item">
                                        <span style={{ fontWeight: 600 }}>{item.type}:</span> {item.name} <span style={{ color: 'var(--text-secondary)', fontSize: '0.9em' }}>({item.id})</span>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </>
                          )}
                          {user && section === 'files' && (
                            <FileView
                              files={fileList}
                              onFileUpload={handleFileUpload}
                              onFileDelete={handleDeleteFile}
                              onFileCopy={handleCopy}
                              onFileDownload={handleFileDownload}
                              onFileUpdate={handleFileUpdate}
                            />
                          )}
                          {user && section === 'images' && (
                            <ImageView
                              images={imageList}
                              onImageUpload={handleImageUpload}
                              onImageDelete={handleDeleteImage}
                              onImageCopy={handleCopy}
                              onImageView={handleImageView}
                              onImageUpdate={handleImageUpdate}
                            />
                          )}
                          {user && section === 'pastes' && (
                            <div>
                              <div className="flex-between mb-4">
                                <h2 className="mt-0 mb-0">Pastes</h2>
                                <button onClick={() => setPasteModalOpen(true)} className="btn btn-primary">+ New Paste</button>
                              </div>
                              <PasteModal open={pasteModalOpen} onClose={() => setPasteModalOpen(false)} onSave={handlePasteModalSave} />
                              <div>
                                {pasteList.length === 0 && <div className="text-center text-secondary" style={{ padding: '2rem' }}>No pastes yet.</div>}
                                {pasteList.map(p => (
                                  <div key={p.id} className="card">
                                    <div className="flex-between mb-4">
                                      <div style={{ fontWeight: 600 }}>ID: {p.id}</div>
                                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button onClick={() => handleCopy(`${window.location.origin}/paste/${p.id}`)} title="Copy URL" className="btn-icon">📋</button>
                                        <button onClick={() => handleDeletePaste(p.id)} title="Delete" className="btn-icon" style={{ color: 'var(--danger)' }}>🗑️</button>
                                      </div>
                                    </div>
                                    <SyntaxHighlight text={p.text} language={p.language || 'plaintext'} />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {user && section === 'urls' && (
                            <div>
                              <div className="flex-between mb-4">
                                <h2 className="mt-0 mb-0">Short URLs</h2>
                                <button onClick={() => setShortUrlModalOpen(true)} className="btn btn-primary">+ Shorten URL</button>
                              </div>
                              <ShortUrlModal open={shortUrlModalOpen} onClose={() => setShortUrlModalOpen(false)} onSave={handleShortUrlModalSave} />
                              <div className="table-container">
                                <table className="table">
                                  <thead>
                                    <tr>
                                      <th>Short URL</th>
                                      <th>Original URL</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {urlList.length === 0 && (
                                      <tr><td colSpan={3} className="text-center text-secondary" style={{ padding: '2rem' }}>No short URLs yet.</td></tr>
                                    )}
                                    {urlList.map(u => (
                                      <tr key={u.short}>
                                        <td><a href={u.short} target="_blank" rel="noopener noreferrer">{u.short}</a></td>
                                        <td style={{ wordBreak: 'break-all' }}>{u.original}</td>
                                        <td>
                                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <button onClick={() => handleCopy(u.short)} title="Copy Link" className="btn-icon">📋</button>
                                            <button onClick={() => handleDeleteShortUrl(u.short)} title="Delete" className="btn-icon" style={{ color: 'var(--danger)' }}>🗑️</button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                        </div>
                      </div>
                    </div>
                  } />
                </>
              )}
            </Routes>
          </>
        )}
      </Router>
    );
}

export default App;
